/**
 * Compounding Effects Configuration
 * These parameters model how SAFECARE reforms change growth trajectories over time
 */

export const compoundingEffectsConfig = {

    // ============================================================================
    // BASELINE GROWTH RATES (Current System)
    // ============================================================================
    baselineGrowth: {
        hospitalCosts: 0.055,        // 5.5% annual (CBO baseline)
        physicianCosts: 0.060,       // 6.0% annual
        drugCosts: 0.065,            // 6.5% annual (price growth)
        adminCosts: 0.040,           // 4.0% annual (complexity arms race)
        capitalCosts: 0.045,         // 4.5% annual (debt service + depreciation)
        overallNHE: 0.059,           // 5.9% annual (CBO long-term projection)
    },

    // ============================================================================
    // 1. NET VS GROSS PAY EFFECT
    // ============================================================================
    netVsGrossPayEffect: {
        enabled: true,

        // Current system household costs eliminated under SAFECARE
        eliminatedCosts: {
            healthInsurancePremium: 12000,      // Annual family premium
            outOfPocketMedical: 5000,           // Annual OOP spending
            perWorkerSavings: 17000,            // Total eliminated per working household
        },

        // Impact on wage growth
        wageGrowthModeration: {
            immediate: 0.000,                    // Year 1: No effect yet (existing contracts)
            year2to5: 0.005,                     // Years 2-5: 0.5pp slower wage growth
            year6to10: 0.008,                    // Years 6-10: 0.8pp slower
            year11plus: 0.008,                   // Years 11+: Stabilizes at 0.8pp

            economicMechanism: "Workers tolerate slower nominal wage growth when net income rises due to eliminated premiums/OOP costs"
        },

        // Phasing: Takes 3 years for labor market to adjust
        adjustmentPeriod: 3,
    },

    // ============================================================================
    // 2. MEDICAL EDUCATION DEBT ELIMINATION
    // ============================================================================
    medicalEducationEffect: {
        enabled: true,

        // Current state
        currentDebtBurden: {
            averageMedSchoolDebt: 200000,       // Per physician
            averageNursingDebt: 50000,          // Per nurse
            monthlyPayment: {
                physician: 2000,                   // 10-year standard repayment
                nurse: 500,
            },
            debtServicePeriod: 10,               // Years to pay off
        },

        // SAFECARE eliminates debt for new graduates (Title V)
        debtFreeGraduates: {
            startYear: 2027,                     // First debt-free cohort
            percentPerYear: 0.05,                // 5% of workforce replaced annually
            fullReplacementYear: 20,             // Years until 100% workforce debt-free
        },

        // Impact on required salaries
        salaryReductionEffect: {
            perPhysician: 24000,                 // Annual salary can be $24k less with no debt
            perNurse: 6000,                      // Annual salary can be $6k less
            percentReduction: 0.10,              // 10% salary reduction tolerance

            economicMechanism: "Debt-free workers have lower reservation wage, shifting labor supply curve right"
        },

        // Phase-in timeline
        impactCurve: {
            // What % of workforce is debt-free each year
            calculateDebtFreePercent: (year) => {
                const yearsActive = year - 2027;
                if (yearsActive <= 0) return 0;
                if (yearsActive >= 20) return 1.0;
                return yearsActive * 0.05; // Linear replacement
            }
        }
    },

    // ============================================================================
    // 3. ADMINISTRATIVE TIME LIBERATION
    // ============================================================================
    administrativeTimeLiberationEffect: {
        enabled: true,

        // Current physician time allocation
        currentTimeAllocation: {
            patientCare: 0.60,                   // 60% clinical time
            billingPaperwork: 0.25,              // 25% billing/insurance
            authorization: 0.15,                 // 15% prior auth/appeals
        },

        // SAFECARE time allocation
        safecareTimeAllocation: {
            patientCare: 0.85,                   // 85% clinical time
            simpleDocumentation: 0.15,           // 15% basic charting only
        },

        // Productivity gain
        productivityIncrease: 0.42,            // 42% more patients per physician

        // Impact on workforce needs
        workforceGrowthReduction: {
            immediate: 0.000,                    // Year 1: No effect (learning curve)
            year2to5: 0.002,                     // Years 2-5: 0.2pp less workforce growth needed
            year6to10: 0.004,                    // Years 6-10: 0.4pp less
            year11plus: 0.004,                   // Years 11+: Stable

            economicMechanism: "Each physician sees more patients → fewer physicians needed → lower demand → slower wage growth"
        },

        adjustmentPeriod: 2,                   // Years for workflow optimization
    },

    // ============================================================================
    // 4. DRUG R&D EFFICIENCY CASCADE
    // ============================================================================
    drugRDEfficiencyEffect: {
        enabled: true,

        // Current R&D allocation (wasteful)
        currentRDAllocation: {
            totalSpending: 100e9,                // $100B annually
            novelTherapies: 0.40,                // 40% real innovation
            meTooPatentExtensions: 0.30,         // 30% duplicative
            marketingAsResearch: 0.20,           // 20% marketing disguised as R&D
            overhead: 0.10,                      // 10% overhead
        },

        // SAFECARE R&D allocation (efficient)
        safecareRDAllocation: {
            totalSpending: 100e9,                // Same total $ (maintained)
            novelTherapies: 0.70,                // 70% real innovation
            publicHealthResearch: 0.20,          // 20% coordinated public health
            meTooPatentExtensions: 0.05,         // 5% (only genuine improvements)
            overhead: 0.05,                      // 5% streamlined overhead
        },

        // Impact on innovation output
        innovationMultiplier: 1.75,            // 75% more effective innovation per dollar

        // Impact on drug price growth
        drugPriceGrowthReduction: {
            immediate: 0.000,                    // Year 1: No pipeline effect yet
            year2to5: 0.005,                     // Years 2-5: 0.5pp slower growth
            year6to10: 0.011,                    // Years 6-10: 1.1pp slower (new drugs cheaper)
            year11plus: 0.011,                   // Years 11+: Stable

            economicMechanism: "Eliminating me-too drugs + IRP → less innovation waste → more cost-effective new drugs"
        },

        pipelineDelay: 5,                      // Years for R&D changes to affect market
    },

    // ============================================================================
    // 5. CAPITAL FINANCING COST ELIMINATION
    // ============================================================================
    capitalFinancingEffect: {
        enabled: true,

        // Current system: Bond financing
        currentFinancing: {
            averageHospitalCost: 500e6,          // $500M per new hospital
            bondInterestRate: 0.055,             // 5.5% municipal bonds
            bondTerm: 30,                        // 30-year bonds
            totalCost: 1038e6,                   // $1,038M with interest
            annualDebtService: 34.6e6,           // $34.6M per year
        },

        // SAFECARE: Direct capital grants (Section 601)
        safecareFinancing: {
            directGrant: 500e6,                  // $500M upfront
            interestCost: 0,                     // $0 interest
            totalCost: 500e6,                    // $500M total
            annualDebtService: 0,                // $0 ongoing
        },

        // System-wide impact
        totalUSHospitals: 6000,
        percentCarryingDebt: 0.60,             // 60% have outstanding bonds
        averageOutstandingDebt: 200e6,         // $200M per hospital with debt

        // Annual interest savings
        annualInterestSavings: function () {
            return this.totalUSHospitals *
                this.percentCarryingDebt *
                this.averageOutstandingDebt *
                this.currentFinancing.bondInterestRate;
        },

        // Legacy debt pays off over time
        debtReductionCurve: {
            calculateRemainingDebt: (year) => {
                const yearsActive = year - 2027;
                if (yearsActive <= 0) return 1.0;   // 100% legacy debt remains
                if (yearsActive >= 30) return 0.0;  // All legacy debt paid off
                return 1.0 - (yearsActive / 30);    // Linear amortization
            }
        },

        // Impact on healthcare costs
        capitalCostGrowthReduction: {
            immediate: 0.003,                     // Year 1: 0.3pp (new construction only)
            year10: 0.010,                        // Year 10: 1.0pp (33% legacy debt gone)
            year20: 0.020,                        // Year 20: 2.0pp (67% legacy debt gone)
            year30plus: 0.030,                    // Year 30+: 3.0pp (all legacy debt gone)

            economicMechanism: "Eliminating bond interest converts financial overhead to productive capital"
        }
    },

    // ============================================================================
    // 6. PREVENTIVE CARE HEALTH CAPITAL ACCUMULATION
    // ============================================================================
    preventiveCareEffect: {
        enabled: true,

        // Example: Diabetes screening
        diabetesExample: {
            screeningCost: 50,                    // $50 per test
            currentScreeningRate: 0.50,           // 50% (due to copays)
            safecareScreeningRate: 0.95,          // 95% ($0 copay)

            lateComplicationCost: 90000,          // $90k lifetime with late diagnosis
            earlyManagementCost: 8000,            // $8k lifetime with early diagnosis
            savingsPerPatient: 82000,             // $82k saved per early catch
        },

        // Population-level impact
        populationHealth: {
            currentComplicationRate: 0.25,        // 25% develop complications
            safecareComplicationRate: 0.03,       // 3% (due to early screening)
            prevalence: 0.11,                     // 11% of population has diabetes

            annualSavingsPerCapita: function (year, population) {
                const yearsActive = year - 2027;
                const screeningRateIncrease = Math.min(0.95 - 0.50, yearsActive * 0.09); // 9pp/year uptake
                const complicationReduction = screeningRateIncrease * (0.25 - 0.03);
                const diabeticPopulation = population * this.prevalence;
                return diabeticPopulation * complicationReduction * 82000 / 20; // Amortize over 20 years
            }
        },

        // Apply to all preventable conditions
        overallHealthImpact: {
            targetConditions: [
                'diabetes', 'hypertension', 'high_cholesterol',
                'colorectal_cancer', 'breast_cancer', 'cervical_cancer'
            ],
            averageSavingsMultiplier: 3.5,        // Diabetes example × 3.5 for all conditions

            // Impact on utilization growth
            utilizationGrowthReduction: {
                immediate: 0.000,                   // Year 1: No population health effect yet
                year5: 0.001,                       // Year 5: 0.1pp (early gains)
                year10: 0.002,                      // Year 10: 0.2pp (compounding)
                year15: 0.003,                      // Year 15: 0.3pp (next generation)
                year20plus: 0.004,                  // Year 20+: 0.4pp (sustained)

                economicMechanism: "Healthier population → fewer acute episodes → less utilization → slower cost growth"
            }
        }
    },

    // ============================================================================
    // 7. ADMINISTRATIVE COMPLEXITY CASCADE (BIGGEST EFFECT)
    // ============================================================================
    administrativeComplexityCascadeEffect: {
        enabled: true,

        // Current system: Billing arms race
        billingArmsRace: {
            currentAdminPercent: 0.22,            // 22% of healthcare costs
            adminGrowthRate: 0.08,                // Grows 8%/year (faster than medical!)

            mechanismDescription: [
                "Insurers deny 20% of claims",
                "Providers hire more billers to appeal",
                "Insurers hire more reviewers to catch upcoding",
                "Providers hire more coders to maximize reimbursement",
                "Cycle repeats → exponential administrative growth"
            ]
        },

        // SAFECARE: Single system eliminates arms race
        simplifiedSystem: {
            targetAdminPercent: 0.02,             // 2% (Canada's level)
            transitionPeriod: 10,                 // 10 years to reach target
            adminReductionRate: -0.05,            // SHRINKS 5% per year during transition

            reductionCurve: function (year) {
                const yearsActive = year - 2027;
                if (yearsActive <= 0) return 0.22;  // Start at 22%
                if (yearsActive >= 10) return 0.02; // End at 2%

                // Exponential decay (faster at first, then slows)
                const decayRate = Math.log(0.02 / 0.22) / 10;
                return 0.22 * Math.exp(decayRate * yearsActive);
            }
        },

        // This is NEGATIVE growth (admin costs shrink)
        adminGrowthModification: {
            year1to5: -0.05,                      // Shrinks 5%/year
            year6to10: -0.03,                     // Shrinks 3%/year (slower as approach target)
            year11plus: 0.00,                     // Stable at 2% of costs

            economicMechanism: "Single billing system eliminates zero-sum complexity competition"
        },

        // Workforce transition
        displacedAdminWorkers: 2700000,         // 2.7M admin workers freed
        retrainingProgram: {
            funded: true,                         // Section 501(b) - severance + retraining
            months: 6,                            // 6 months severance
            retrainingSuccess: 0.75,              // 75% successfully retrain
            newRoles: ['clinical support', 'patient navigation', 'health IT']
        }
    }
};

// ============================================================================
// AGGREGATE EFFECTS CALCULATOR
// ============================================================================
// ============================================================================
// AGGREGATE EFFECTS CALCULATOR
// ============================================================================

/**
 * Helper to merge overrides with base config
 */
export function getEffectiveCompoundingConfig(overrides) {
    if (!overrides || typeof overrides !== 'function') return compoundingEffectsConfig;

    // If overrides is a function (structuralOverrides pattern), apply it
    try {
        const structuralPatch = overrides(compoundingEffectsConfig);

        // Deep merge the patch onto the base config (simplified depth for now)
        // We only expect specific high-level overrides usually
        // For deep merging, we might need Lodash or a recursive helper, 
        // but for now let's assume specific keys are replaced or we do a shallow merge of sub-objects

        const effective = JSON.parse(JSON.stringify(compoundingEffectsConfig)); // Deep clone base

        // Apply patch
        for (const [key, value] of Object.entries(structuralPatch)) {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Merge sub-object (level 1 depth)
                effective[key] = { ...effective[key], ...value };
            } else {
                effective[key] = value;
            }
        }
        return effective;
    } catch (e) {
        console.error("Error applying structural overrides:", e);
        return compoundingEffectsConfig;
    }
}

/**
 * Calculate the aggregate growth reduction with optional overrides
 * @param {number} year 
 * @param {Function|Object} structuralOverrides - Optional function to modify config context
 */
export function calculateAggregateGrowthEffect(year, structuralOverrides = null) {
    const config = getEffectiveCompoundingConfig(structuralOverrides);
    const yearsActive = year - 2027;

    let totalEffect = 0;

    // 1. Net vs gross pay (0-0.8pp)
    if (config.netVsGrossPayEffect.enabled) {
        if (yearsActive <= 1) totalEffect += 0;
        else if (yearsActive <= 5) totalEffect += 0.005;
        else totalEffect += 0.008;
    }

    // 2. Medical education debt (0-1.2pp, phases in over 20 years)
    if (config.medicalEducationEffect.enabled) {
        const debtFreePercent = Math.min(yearsActive * 0.05, 1.0);
        totalEffect += debtFreePercent * 0.012;
    }

    // 3. Administrative time (0-0.4pp)
    if (config.administrativeTimeLiberationEffect.enabled) {
        if (yearsActive <= 1) totalEffect += 0;
        else if (yearsActive <= 5) totalEffect += 0.002;
        else totalEffect += 0.004;
    }

    // 4. Drug R&D efficiency (0-1.1pp)
    if (config.drugRDEfficiencyEffect.enabled) {
        if (yearsActive <= 5) totalEffect += yearsActive * 0.001;
        else totalEffect += 0.011;
    }

    // 5. Capital financing (0-3.0pp, over 30 years)
    if (config.capitalFinancingEffect.enabled) {
        if (yearsActive <= 1) totalEffect += 0.003;
        else if (yearsActive <= 10) totalEffect += 0.003 + (yearsActive / 10) * 0.007;
        else if (yearsActive <= 20) totalEffect += 0.010 + ((yearsActive - 10) / 10) * 0.010;
        else if (yearsActive <= 30) totalEffect += 0.020 + ((yearsActive - 20) / 10) * 0.010;
        else totalEffect += 0.030;
    }

    // 6. Preventive care (0-0.4pp)
    if (config.preventiveCareEffect.enabled) {
        if (yearsActive <= 5) totalEffect += 0;
        else if (yearsActive <= 10) totalEffect += 0.001 + ((yearsActive - 5) / 5) * 0.001;
        else if (yearsActive <= 15) totalEffect += 0.002 + ((yearsActive - 10) / 5) * 0.001;
        else totalEffect += 0.004;
    }

    // 7. Admin cascade (0-0.9pp, negative admin growth)
    if (config.administrativeComplexityCascadeEffect.enabled) {
        if (yearsActive <= 5) totalEffect += 0.009 * yearsActive;
        else if (yearsActive <= 10) totalEffect += 0.045 + ((yearsActive - 5) / 5) * 0.045;
        else totalEffect += 0.09;
    }

    return Math.min(totalEffect, 0.049); // Cap at 4.9pp total
}

// Conservative adjustment factor
export const CONSERVATIVE_FACTOR = 0.61; // Use 61% of calculated effect

export function getConservativeGrowthReduction(year, structuralOverrides = null) {
    return calculateAggregateGrowthEffect(year, structuralOverrides) * CONSERVATIVE_FACTOR;
}

/**
 * DEPRECATED: Old mutation method. 
 * Kept strictly for backward compatibility if called, but does nothing or logs warning.
 * The new system passes overrides via arguments.
 */
export function applyStagnationOverrides() {
    console.warn("applyStagnationOverrides is deprecated. Use structuralOverrides in runScenario instead.");
    return "This function is deprecated and no longer mutates global state.";
}
